/* eslint-disable dot-notation */
import axios from "axios";
import history from "utils/history";
import { getBearer, getLanguage, removeUser } from "./userStorage";

export const HTTP_METHODS = {
  GET: "get",
  HEAD: "head",
  OPTIONS: "options",
  DELETE: "delete",
  POST: "post",
  PUT: "put",
  PATCH: "patch",
};

// Standard request configuration for the project
export const api = axios.create({
  baseURL: __URLS__["api"],
  headers: {
    "Content-type": "application/json",
  },
});

export function buildRequestOptions(options, url = "") {
  return {
    method: options.method || HTTP_METHODS.GET,
    config: options.config || {},
    instance: options.instance || api,
    body: options.body || "",
    params: options.params || {},
    cancelToken: options.cancelToken || null,

    hasParams() {
      return Object.keys(this.params).length > 0;
    },

    get url() {
      let paramUrl = url;

      // Add optional parameters to the url if parameters are set
      if (this.hasParams()) {
        if (paramUrl[paramUrl.length - 1] === "/") {
          paramUrl = paramUrl.slice(0, paramUrl.length - 1);
        }
        paramUrl += "?";
        Object.keys(this.params).forEach((key, index) => {
          if (index !== 0) {
            paramUrl += "&";
          }
          paramUrl += `${key}=${this.params[key]}`;
        });
      }

      return paramUrl;
    },

    get urlPlain() {
      return url;
    },

    hasBody() {
      return (
        this.method === HTTP_METHODS.POST ||
        this.method === HTTP_METHODS.PUT ||
        this.method === HTTP_METHODS.PATCH ||
        this.method === HTTP_METHODS.DELETE
      );
    },

    dataBody() {
      return this.method === HTTP_METHODS.DELETE;
    },
  };
}
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
export function parseJSON(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.data;
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
export function checkStatus(response) {
  if (response.status === 401) {
    removeUser();
    history.push({ pathName: "/login", type: response.data.type });
  }
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error(response.statusText);
  error.response = response;
  if (response.status === 500) {
    error.response.data = { message: {} };
  }
  throw error;
}

export default async function request(url, options = {}, skipParse = false) {
  const reqOpts = buildRequestOptions(options, url);

  // Make the request
  let res = null;
  try {
    if (reqOpts.hasBody()) {
      if (reqOpts.dataBody()) {
        reqOpts.config.data = reqOpts.body;
        res = await reqOpts.instance[reqOpts.method](
          reqOpts.url,
          reqOpts.config,
        );
      } else {
        res = await reqOpts.instance[reqOpts.method](
          reqOpts.url,
          reqOpts.body,
          reqOpts.config,
        );
      }
    } else {
      res = await reqOpts.instance[reqOpts.method](reqOpts.url, reqOpts.config);
    }
  } catch (e) {
    return checkStatus(e.response);
  }

  // Handle the result
  res = await checkStatus(res);
  if (!skipParse) {
    res = await parseJSON(res);
  }
  return res;
}

export function requestNoParse(url, options) {
  return request(url, options, true);
}

export function authorizedRequest(url, options = {}) {
  const reqOpts = buildRequestOptions(options, url);

  if (!reqOpts.config.headers) {
    reqOpts.config.headers = {};
  }
  reqOpts.config.headers["Authorization"] = `Bearer ${getBearer()}`;
  reqOpts.config.headers["Accept-Language"] = `${getLanguage()}`;

  return request(url, reqOpts);
}

export { request };
